/** Benvenuto al primo accesso: cosa fare e soprattutto perché. Diverso per titolare e per ruolo. */

export const gruppoBenvenuto = (user) => {
  if (user?.can_send === false) return "reparto";
  const r = user?.role;
  if (r === "titolare") return "titolare";
  if (["cuoco", "pizzaiolo", "pasticciere", "barista"].includes(r)) return "reparto";
  if (["cassiere", "direttore"].includes(r)) return "cassa";
  return "sala";
};

const IT = {
  ui: { next: "Avanti", back: "Indietro", done: "Ho capito", skip: "Salta", again: "Rivedi la guida",
        manual: "Apri il manuale completo", of: "{a} di {b}", guideTitle: "Guida rapida",
        guideSub: "Le poche cose che ti servono per lavorare senza chiedere niente a nessuno." },
  titolare: {
    hello: "Benvenuto in Reprehendo",
    sub: "Quattro cose in due minuti: dopo il locale cammina da solo.",
    slides: [
      { t: "Crea i profili del tuo staff",
        why: "Perché conta: ogni operazione resta a nome di chi l'ha fatta, ognuno vede solo ciò che gli serve e i PIN sono personali — non li vedi nemmeno tu.",
        p: ["Vai su Persone → Personale e aggiungi una persona per volta: nome e ruolo, il PIN lo fa l'app.",
            "Un profilo per persona, mai uno condiviso: è l'unico modo per sapere chi ha aperto un tavolo o incassato.",
            "Se un PIN gira di bocca in bocca, fai «Nuovo PIN»: il vecchio smette subito di funzionare."] },
      { t: "A ogni persona il suo ruolo",
        why: "Perché conta: il ruolo decide cosa vede. Nessuno deve cercare la propria pagina, e nessuno inciampa dove non deve.",
        p: ["Il cameriere entra in Sala, il cuoco e il pizzaiolo direttamente sul Monitor del proprio reparto, il cassiere in Cassa.",
            "Cuochi, pizzaioli e baristi non vedono i prezzi: lavorano sui piatti, non sui soldi.",
            "Puoi togliere o dare singoli permessi (per esempio prendere prenotazioni) sulla scheda della persona."] },
      { t: "Come far entrare lo staff",
        why: "Perché conta: il codice del locale resta tuo. Lo staff entra con il QR e il proprio PIN, niente codici dettati a voce.",
        p: ["In Personale attiva «Ingresso col QR»: fai inquadrare il QR dai loro telefoni, o mandagli il link.",
            "Il QR da solo non fa entrare nessuno: serve sempre il PIN personale.",
            "Da ogni scheda puoi mandare il messaggio con il PIN su WhatsApp o SMS, o stampare la scheda personale."] },
      { t: "Da qui parte il resto",
        why: "Perché conta: il modo più veloce di imparare è fare un servizio finto prima di quello vero.",
        p: ["In home trovi «Primi passi»: apri un tavolo, prendi una comanda, mandala in cucina, chiudi il conto.",
            "Fallo a locale chiuso, con due piatti finti: in cinque minuti hai visto tutto il giro.",
            "«Guida rapida» resta nel menu, e il manuale completo è stampabile per lasciarlo in cucina."] },
    ],
  },
  sala: {
    hello: "Benvenuto in sala",
    sub: "Tre cose e sei operativo.",
    p: ["**Apri il tavolo, aggiungi i piatti, manda il giro.** Tocca il tavolo, scegli i piatti, poi «Manda»: partono al reparto giusto da soli.",
        "**Il totale si tocca.** Tocca il totale in fondo alla comanda e si apre il conto, anche per dividerlo.",
        "**Se il tavolo cambia idea, «Modifica al volo».** Sulla riga già mandata: aspetta, manda prima, sposta dopo, annulla. Il cuoco risponde con un tocco: non devi più andare in cucina."],
    foot: "Quando ti allontani dal telefono, tocca «Cambia ruolo»: ogni operazione resta a nome tuo.",
  },
  reparto: {
    hello: "Benvenuto al monitor",
    sub: "Tre cose e sei operativo.",
    p: ["**«Da fare ora» è il tuo lavoro adesso.** Sono i giri che la sala ha mandato: hanno il timer e il tasto «Pronto». Tocca «Pronto» quando il piatto esce.",
        "**«In arrivo» serve solo per organizzarti.** È il resto della comanda, non ancora mandato: guardalo per preparare avanti, ma non ha il tasto Pronto — se ci lavori già, segna «Iniziato in anticipo».",
        "**Rispondi alle richieste della sala.** Se una riga lampeggia in rosso, la sala chiede qualcosa: un tocco su «Ok, ricevuto» o «Già in cottura» e loro lo sanno subito."],
    foot: "Accendi la voce: il monitor legge le comande nuove e lo schermo non si spegne più durante il servizio.",
  },
  cassa: {
    hello: "Benvenuto",
    sub: "Tre cose e sei operativo.",
    p: ["**Incassi.** In Cassa vedi i tavoli da chiudere: scegli contanti, carta o misto, e puoi dividere il conto per persona.",
        "**Chiusura serata.** A fine servizio la chiusura mette in fila incassi, scontrini e differenze di cassa: firmi e hai chiuso.",
        "**Report.** In Movimenti e Statistiche vedi cosa è entrato, cosa si è venduto e quali tavoli hanno rallentato il servizio."],
    foot: "Se un fondo cassa non torna, la chiusura ti dice esattamente su quale persona e quale tavolo guardare.",
  },
};

const EN = {
  ui: { next: "Next", back: "Back", done: "Got it", skip: "Skip", again: "See the guide again",
        manual: "Open the full manual", of: "{a} of {b}", guideTitle: "Quick guide",
        guideSub: "The few things you need to work without asking anyone." },
  titolare: {
    hello: "Welcome to Reprehendo",
    sub: "Four things in two minutes, then the place runs on its own.",
    slides: [
      { t: "Create your staff profiles",
        why: "Why it matters: every action stays under the name of who did it, everyone sees only what they need, and PINs are personal — not even you can read them.",
        p: ["Go to People → Staff and add one person at a time: name and role, the app makes the PIN.",
            "One profile per person, never a shared one: it's the only way to know who opened a table or took the money.",
            "If a PIN gets passed around, tap «New PIN»: the old one stops working straight away."] },
      { t: "The right role for each person",
        why: "Why it matters: the role decides what they see. Nobody hunts for their own page, and nobody stumbles where they shouldn't.",
        p: ["Waiters land on the Floor, chefs and pizza chefs straight on their station monitor, cashiers on the Till.",
            "Kitchen and bar staff don't see prices: they work on dishes, not money.",
            "You can grant or remove single permissions (like taking bookings) on each person's card."] },
      { t: "How staff get in",
        why: "Why it matters: the venue code stays yours. Staff get in with the QR and their own PIN, no codes read out loud.",
        p: ["In Staff turn on «QR entry»: let them scan the QR, or send them the link.",
            "The QR alone lets nobody in: the personal PIN is always required.",
            "From each card you can send the PIN by WhatsApp or SMS, or print the personal card."] },
      { t: "Everything else starts here",
        why: "Why it matters: the fastest way to learn is a fake service before the real one.",
        p: ["The home page has «First steps»: open a table, take an order, send it to the kitchen, close the bill.",
            "Do it with the doors closed and two fake dishes: five minutes and you've seen the whole loop.",
            "«Quick guide» stays in the menu, and the full manual is printable to leave in the kitchen."] },
    ],
  },
  sala: {
    hello: "Welcome to the floor",
    sub: "Three things and you're ready.",
    p: ["**Open the table, add dishes, send the round.** Tap the table, pick the dishes, then «Send»: they reach the right station on their own.",
        "**The total is tappable.** Tap the total at the bottom of the order to open the bill, split included.",
        "**If the table changes its mind, «Change on the fly».** On an already sent line: hold, send first, move after, cancel. The chef replies with one tap: no more walking to the kitchen."],
    foot: "When you step away from the phone, tap «Switch role»: every action stays under your name.",
  },
  reparto: {
    hello: "Welcome to the monitor",
    sub: "Three things and you're ready.",
    p: ["**«To do now» is your work right now.** These are the rounds the floor has sent: they have the timer and the «Ready» button. Tap «Ready» when the dish leaves.",
        "**«Coming up» is only to plan ahead.** It's the rest of the order, not sent yet: use it to prep early, but it has no Ready button — if you're already on it, mark «Started early».",
        "**Answer the floor.** If a line flashes red the floor is asking something: one tap on «Ok, got it» or «Already cooking» and they know instantly."],
    foot: "Turn the voice on: the monitor reads out new orders and the screen stays awake through service.",
  },
  cassa: {
    hello: "Welcome",
    sub: "Three things and you're ready.",
    p: ["**Payments.** The Till shows the tables to close: cash, card or mixed, and you can split the bill per person.",
        "**End of night.** The closing lines up takings, receipts and cash differences: sign it and you're done.",
        "**Reports.** Movements and Statistics show what came in, what sold and which tables slowed the service down."],
    foot: "If a cash float doesn't add up, the closing tells you exactly which person and table to look at.",
  },
};

const FR = {
  ui: { next: "Suivant", back: "Retour", done: "Compris", skip: "Passer", again: "Revoir le guide",
        manual: "Ouvrir le manuel complet", of: "{a} sur {b}", guideTitle: "Guide rapide",
        guideSub: "Le minimum pour travailler sans rien demander à personne." },
  titolare: {
    hello: "Bienvenue dans Reprehendo",
    sub: "Quatre choses en deux minutes, ensuite l'établissement tourne seul.",
    slides: [
      { t: "Crée les profils de ton équipe",
        why: "Pourquoi c'est important : chaque action reste au nom de celui qui l'a faite, chacun ne voit que ce qui lui sert, et les codes PIN sont personnels — même toi ne les vois pas.",
        p: ["Va dans Personnes → Personnel et ajoute une personne à la fois : nom et rôle, l'appli crée le PIN.",
            "Un profil par personne, jamais partagé : c'est le seul moyen de savoir qui a ouvert une table ou encaissé.",
            "Si un PIN circule, touche « Nouveau PIN » : l'ancien cesse aussitôt de fonctionner."] },
      { t: "À chacun son rôle",
        why: "Pourquoi c'est important : le rôle décide de ce que la personne voit. Personne ne cherche sa page, personne ne se perd ailleurs.",
        p: ["Le serveur arrive en Salle, le cuisinier et le pizzaïolo directement sur le moniteur de leur poste, le caissier en Caisse.",
            "Cuisine et bar ne voient pas les prix : ils travaillent sur les plats, pas sur l'argent.",
            "Tu peux donner ou retirer des droits précis (par exemple prendre les réservations) sur la fiche de chacun."] },
      { t: "Comment l'équipe entre",
        why: "Pourquoi c'est important : le code de l'établissement reste le tien. L'équipe entre avec le QR et son PIN, sans code dicté à voix haute.",
        p: ["Dans Personnel, active « Entrée par QR » : fais scanner le QR, ou envoie le lien.",
            "Le QR seul ne fait entrer personne : le PIN personnel est toujours nécessaire.",
            "Depuis chaque fiche tu peux envoyer le PIN par WhatsApp ou SMS, ou imprimer la fiche personnelle."] },
      { t: "Tout le reste part d'ici",
        why: "Pourquoi c'est important : le plus rapide pour apprendre, c'est un faux service avant le vrai.",
        p: ["Sur l'accueil il y a « Premiers pas » : ouvre une table, prends une commande, envoie-la, clôture l'addition.",
            "Fais-le porte fermée avec deux plats fictifs : cinq minutes et tu as vu tout le cycle.",
            "« Guide rapide » reste dans le menu, et le manuel complet s'imprime pour la cuisine."] },
    ],
  },
  sala: {
    hello: "Bienvenue en salle",
    sub: "Trois choses et tu es opérationnel.",
    p: ["**Ouvre la table, ajoute les plats, envoie la tournée.** Touche la table, choisis les plats, puis « Envoyer » : ils partent au bon poste tout seuls.",
        "**Le total est cliquable.** Touche le total en bas de la commande pour ouvrir l'addition, partage inclus.",
        "**Si la table change d'avis, « Modifier à la volée ».** Sur une ligne déjà envoyée : attendre, envoyer d'abord, déplacer après, annuler. Le cuisinier répond d'un geste : plus besoin d'aller en cuisine."],
    foot: "Quand tu t'éloignes du téléphone, touche « Changer de rôle » : chaque action reste à ton nom.",
  },
  reparto: {
    hello: "Bienvenue sur le moniteur",
    sub: "Trois choses et tu es opérationnel.",
    p: ["**« À faire maintenant » c'est ton travail présent.** Ce sont les tournées envoyées par la salle : minuteur et bouton « Prêt ». Touche « Prêt » quand le plat sort.",
        "**« À venir » sert seulement à t'organiser.** C'est le reste de la commande, pas encore envoyé : utile pour préparer à l'avance, sans bouton Prêt — si tu t'y mets déjà, marque « Commencé en avance ».",
        "**Réponds à la salle.** Si une ligne clignote en rouge, la salle demande quelque chose : un geste sur « Ok, reçu » ou « Déjà en cuisson » et ils le savent aussitôt."],
    foot: "Active la voix : le moniteur lit les nouvelles commandes et l'écran ne s'éteint plus pendant le service.",
  },
  cassa: {
    hello: "Bienvenue",
    sub: "Trois choses et tu es opérationnel.",
    p: ["**Encaissements.** La Caisse montre les tables à clôturer : espèces, carte ou mixte, avec partage par personne.",
        "**Clôture de la soirée.** En fin de service, la clôture aligne recettes, tickets et écarts de caisse : tu signes, c'est fini.",
        "**Rapports.** Mouvements et Statistiques montrent les entrées, les ventes et les tables qui ont ralenti le service."],
    foot: "Si un fonds de caisse ne tombe pas juste, la clôture indique exactement quelle personne et quelle table regarder.",
  },
};

const DE = {
  ui: { next: "Weiter", back: "Zurück", done: "Verstanden", skip: "Überspringen", again: "Anleitung erneut ansehen",
        manual: "Vollständiges Handbuch öffnen", of: "{a} von {b}", guideTitle: "Kurzanleitung",
        guideSub: "Das Wenige, das du brauchst, um ohne Fragen zu arbeiten." },
  titolare: {
    hello: "Willkommen bei Reprehendo",
    sub: "Vier Dinge in zwei Minuten, danach läuft der Betrieb allein.",
    slides: [
      { t: "Lege die Profile deines Teams an",
        why: "Warum das zählt: jede Handlung bleibt auf den Namen der Person, jede sieht nur das Nötige, und die PINs sind persönlich — auch du siehst sie nicht.",
        p: ["Unter Personen → Personal eine Person nach der anderen anlegen: Name und Rolle, die PIN macht die App.",
            "Ein Profil pro Person, niemals ein gemeinsames: nur so weiß man, wer einen Tisch geöffnet oder kassiert hat.",
            "Wenn eine PIN herumgeht: «Neue PIN» tippen — die alte gilt sofort nicht mehr."] },
      { t: "Jeder Person ihre Rolle",
        why: "Warum das zählt: die Rolle bestimmt, was sichtbar ist. Niemand sucht seine Seite, niemand landet, wo er nicht hingehört.",
        p: ["Service startet im Saal, Küche und Pizzabäcker direkt am Stationsmonitor, Kasse an der Kasse.",
            "Küche und Bar sehen keine Preise: sie arbeiten an Gerichten, nicht am Geld.",
            "Einzelne Rechte (z. B. Reservierungen annehmen) kannst du pro Person geben oder entziehen."] },
      { t: "So kommt das Team hinein",
        why: "Warum das zählt: der Betriebscode bleibt bei dir. Das Team kommt mit QR und eigener PIN hinein, ohne zugerufene Codes.",
        p: ["Unter Personal «QR-Zugang» aktivieren: QR scannen lassen oder den Link senden.",
            "Der QR allein lässt niemanden hinein: die persönliche PIN ist immer nötig.",
            "Aus jeder Karte kannst du die PIN per WhatsApp oder SMS senden oder die persönliche Karte drucken."] },
      { t: "Alles andere beginnt hier",
        why: "Warum das zählt: am schnellsten lernt man mit einem Probedurchlauf vor dem echten Service.",
        p: ["Auf der Startseite gibt es «Erste Schritte»: Tisch öffnen, Bestellung aufnehmen, an die Küche senden, Rechnung schließen.",
            "Mach es bei geschlossener Tür mit zwei Fantasiegerichten: fünf Minuten und der ganze Ablauf ist klar.",
            "«Kurzanleitung» bleibt im Menü, das ganze Handbuch ist druckbar für die Küche."] },
    ],
  },
  sala: {
    hello: "Willkommen im Service",
    sub: "Drei Dinge und du bist bereit.",
    p: ["**Tisch öffnen, Gerichte hinzufügen, Runde senden.** Tisch antippen, Gerichte wählen, dann «Senden»: sie erreichen die richtige Station von selbst.",
        "**Die Summe ist antippbar.** Tippe unten auf die Summe, dann öffnet sich die Rechnung, auch zum Teilen.",
        "**Ändert der Tisch seine Meinung: «Änderung sofort».** Auf einer gesendeten Zeile: warten, vorziehen, nach hinten schieben, stornieren. Die Küche antwortet mit einem Tipp — kein Weg mehr in die Küche."],
    foot: "Wenn du das Telefon weglegst, tippe «Rolle wechseln»: jede Handlung bleibt auf deinen Namen.",
  },
  reparto: {
    hello: "Willkommen am Monitor",
    sub: "Drei Dinge und du bist bereit.",
    p: ["**«Jetzt zu machen» ist deine aktuelle Arbeit.** Das sind die gesendeten Runden: mit Timer und «Fertig»-Taste. Tippe «Fertig», wenn das Gericht rausgeht.",
        "**«Kommt noch» dient nur zur Planung.** Der Rest der Bestellung, noch nicht gesendet: gut zum Vorbereiten, ohne Fertig-Taste — wenn du schon dran bist, markiere «Früher angefangen».",
        "**Antworte dem Service.** Blinkt eine Zeile rot, fragt der Service etwas: ein Tipp auf «Ok, verstanden» oder «Schon am Kochen» und sie wissen es sofort."],
    foot: "Schalte die Stimme ein: der Monitor liest neue Bestellungen vor und der Bildschirm bleibt an.",
  },
  cassa: {
    hello: "Willkommen",
    sub: "Drei Dinge und du bist bereit.",
    p: ["**Kassieren.** Die Kasse zeigt die Tische zum Abschließen: bar, Karte oder gemischt, Teilung pro Person möglich.",
        "**Tagesabschluss.** Am Ende stellt der Abschluss Einnahmen, Belege und Kassendifferenzen gegenüber: unterschreiben und fertig.",
        "**Berichte.** Bewegungen und Statistiken zeigen Einnahmen, Verkäufe und welche Tische den Service gebremst haben."],
    foot: "Stimmt ein Kassenbestand nicht, sagt der Abschluss genau, welche Person und welcher Tisch zu prüfen sind.",
  },
};

const ALL = { it: IT, en: EN, fr: FR, de: DE };

export const benvenuto = (lang) => ALL[lang] || IT;
